const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/casesReducer.ts';
import Imm from 'immutable';
import _ from 'lodash';
import moment from 'moment';
import { Logger } from '../services/KisLoggerService';
import { ILoadCaseRecordAction, ILoadCasesAction, LOAD_CASE_RECORD, LOAD_CASES } from '../actions/casesActions';
import { atSetCurrentPatNrFallNr, LOAD_PATIENT, SetCurrentPatNrFallNrAction } from '../actions/patientsActions';
import { ReducerState } from '../defs/actionTypes';
import {
    defaultKisApiActionFailure,
    defaultKisApiActionPending,
    defaultKisApiActionSuccess,
    getDataFromPayloadWithListObject,
} from '@cistec/common_web/utils/redux4Requests';

const Log = Logger('common/casesReducer');

/**
 * The initial values of this reducer's branch (common values only).
 */
export const initialState = Imm.fromJS({
    dischargeMgmt: {},
});

/**
 * The actual reducer
 */
export function casesReducer(state: any = initialState, action: any = {}) {
    const LOAD_CASES_KEYS = ['raw', action.meta?.patnr];

    switch (action.type) {
        case LOAD_PATIENT.success:
            return loadPatientSuccessful(state, action.payload);

        case LOAD_CASES.pending:
            state = defaultKisApiActionPending(state, LOAD_CASES_KEYS, action);
            return loadCasesPending(state, action);
        case LOAD_CASES.success:
            state = defaultKisApiActionSuccess(state, getDataFromPayloadWithListObject(action), LOAD_CASES_KEYS);
            return loadCasesSuccess(state, action);
        case LOAD_CASES.failure:
            state = defaultKisApiActionFailure(state, LOAD_CASES_KEYS, action);
            return loadCasesFailure(state, action);

        case LOAD_CASE_RECORD.pending:
            return loadCaseRecordPending(state, action);
        case LOAD_CASE_RECORD.success:
            return loadCaseRecordSuccess(state, action);
        case LOAD_CASE_RECORD.failure:
            return loadCaseRecordFailure(state, action);

        case atSetCurrentPatNrFallNr:
            return handleSetCurrentPatNrFallNr(state, action);

        default:
            return state;
    }
}

/**
 * Called when a single patient record has been loaded. Depending on the
 * endpoint that was used to load the patient, the payload may contain
 * information about the case. If this is true, we put that information into
 * our store.
 */
function loadPatientSuccessful(state: Imm.Map<string, any>, payload: any) {
    return extractCase(state, payload);
}

/**
 * Called when a patient's list of cases is being retrieved.
 */
function loadCasesPending(state: Imm.Map<string, any>, action: ILoadCasesAction) {
    const patnr = !action || !action.meta ? null : action.meta.patnr;
    return state.setIn(['map', patnr, 'loading'], true);
}

/**
 * Called when a patient's list of cases has been retrieved successfully.
 */
function loadCasesSuccess(state: Imm.Map<string, any>, action: ILoadCasesAction) {
    const patnr = !action || !action.meta ? null : action.meta.patnr;

    if (!patnr) {
        Log.error('Missing patnr in action.meta!');
        return state;
    }

    const data = !action || !action.payload ? null : action.payload;

    if (!data || !data.List) {
        Log.warn(`The received data is empty! patnr=${patnr}, data=`, data);
    } else {
        if (!Array.isArray(data.List)) {
            // List may occasionally be an object.
            data.List = _.values(data.List);
        }

        const allCasesAreClosed = data.List.reduce((allClosed: boolean, rec: any) => {
            return allClosed && moment().isAfter(rec.AUSDAT || '3000-01-01 01:00:00', 'day');
        }, true);

        data.List.forEach((entry: any) => {
            if (action.meta.storeFullRecords) {
                const rec = Imm.fromJS(entry);
                const fallnr = rec.get('FALLNR');

                if (!fallnr) {
                    Log.warn('Skipping entry with missing FALLNR', entry);
                } else {
                    state = state.setIn(['map', patnr, 'data', fallnr], true).setIn(['cases', fallnr], rec);
                }
            } else {
                state = extractCase(state, entry, true, true, patnr, allCasesAreClosed);
            }
        });
    }

    return state.setIn(['map', patnr, 'loading'], false);
}

/**
 * Called when a patient's list of cases could not be retrieved.
 */
function loadCasesFailure(state: Imm.Map<string, any>, action: ILoadCasesAction) {
    const patnr = !action || !action.meta ? null : action.meta.patnr;
    Log.warn('Failed to retrieve cases, patnr=' + patnr);
    return state.setIn(['map', patnr, 'loading'], false);
}

/**
 * Called upon receiving LOAD_CASE_RECORD_PENDING
 */
function loadCaseRecordPending(state: Imm.Map<string, any>, action: ILoadCaseRecordAction) {
    return state.setIn(['cases', action.meta.fallnr, 'loading'], true);
}

/**
 * Called upon receiving LOAD_CASE_RECORD.success
 */
function loadCaseRecordSuccess(state: Imm.Map<string, any>, action: ILoadCaseRecordAction) {
    state = extractCase(state, action.payload, true, true);
    return state.deleteIn(['cases', action.meta.fallnr, 'loading']);
}

/**
 * Called upon receiving LOAD_CASE_RECORD.failure
 */
function loadCaseRecordFailure(state: Imm.Map<string, any>, action: ILoadCaseRecordAction) {
    return state.setIn(['cases', action.meta.fallnr, 'loading'], 'failed');
}

/**
 * Helper function for extracting the relevant case information from the given payload.
 * @param state
 * @param payload
 * @param caseRequired
 * @param overwrite
 * @param mustMatchPatnr
 * @param selectClosedCase  If all cases are closed, the automatic case selection must select a closed case.
 */
function extractCase(
    state: Imm.Map<string, any>,
    payload: any,
    caseRequired?: boolean,
    overwrite?: boolean,
    mustMatchPatnr?: string,
    selectClosedCase?: boolean
) {
    const caseContent = payload?.Std ?? payload;
    const specificContent = payload?.Specific ?? undefined;
    const patnr = caseContent.PATNR;
    const fallnr = caseContent.FALLNR;
    // Log.info ('Case payload', caseContent);

    if (!patnr) {
        Log.warn('Missing PATNR in payload!', caseContent);
    } else if (mustMatchPatnr && mustMatchPatnr !== patnr) {
        Log.warn(`PATNR in payload does not match! patnr=${patnr}, payload:`, caseContent);
    } else if (!fallnr) {
        // The payload does not contain any information about the case.
        if (caseRequired) {
            Log.warn('Missing FALLNR in payload!', caseContent);
        }
    } else if (!overwrite && state.getIn(['map', patnr, 'data', fallnr])) {
        // The case is already in the store. We do NOT update the
        // information, because the store may contain more data
        // than what's included in the payload.
    } else {
        // A patient can have any number of cases, but each case
        // belongs to exactly one patient. The following map may
        // be used to quickly find the cases of a given patient,
        // or to check if a given case belongs to a given patient.
        state = state.setIn(['map', patnr, 'data', fallnr], true);

        // Determine the currently selected case in an intelligent way.

        if (state.getIn(['map', patnr, 'current'])) {
            // A case has already been selected, either automatically,
            // or manually (by the user). Leave it at that!
        } else if (caseContent.STATUS === 'I') {
            // This case is closed and cannot be selected.
        } else if (!selectClosedCase && moment().isAfter(caseContent.AUSDAT || '3000-01-01 01:00:00', 'day')) {
            // The Austrittsdatum has already been set to today or even further into the past. While we still may
            // allow the user to manually select the case, we should not select it automatically if there are open
            // cases.
        } else {
            // We pick this case as the default!
            state = state.setIn(['map', patnr, 'current'], fallnr);
        }

        // Since the map (above) establishes the patient/case
        // relationship, we can keep the list of cases flat.
        state = state.setIn(
            ['cases', fallnr],
            Imm.fromJS({
                // Mention the members to be copied into the store
                // explicitly, throwing away any Mandate-specific data
                // that's currently sent by KISAPI.
                PATNR: caseContent.PATNR,
                FALLNR: caseContent.FALLNR,
                FKEY: caseContent.FKEY,
                OCODE: caseContent.OCODE || caseContent.FKEY,
                KLINIK: caseContent.KLINIK,
                KLASSE: caseContent.KLASSE,
                VERSICHERUNGSKLASSE: caseContent.CONTENT.Versicherungsklasse,
                FALLART: caseContent.FALLART,
                STATUS: caseContent.STATUS,
                EINDAT: caseContent.EINDAT,
                AUSDAT: caseContent.AUSDAT,
                ROOM: caseContent.CONTENT.Zimmer,
                BED: caseContent.CONTENT.Bett,
                FALLNR_REL: specificContent?.FALLNR_REL || undefined,
                HOTELZUSCHLAG: specificContent?.HOTELZUSCHLAG || undefined,
                VERSICHERUNGSMODELL: specificContent?.VERSICHERUNGSMODELL || undefined,
                AUFNAHMEGRUND: specificContent?.AUFNAHMEGRUND || undefined,
            })
        );
    }

    return state;
}

function handleSetCurrentPatNrFallNr(state: ReducerState, action: SetCurrentPatNrFallNrAction): ReducerState {
    return !action.fallnr ? state : state.setIn(['map', action.patnr, 'current'], action.fallnr);
}
